/**
 * Save the current Performance selection to a baseline.
 *
 * Press `+` with events lassoed (or a cut active) and a small modal offers the project's baselines
 * plus "New baseline". Performance and maneuvers share those names: saving maneuvers onto a
 * performance baseline adds them to it. Saving posts the member event ids together with the
 * `(channel, aggregate)` pairs the page's charts are showing, and the server fits the curves.
 */

import { createSignal, type Accessor } from 'solid-js';
import { selectedEvents, cutEvents } from '../store/selectionStore';
import { user } from '../store/userStore';
import { isAdministrator } from '../utils/userPermissions';
import { debug as logDebug, error as logError } from '../utils/console';
import {
  listBaselines,
  createBaseline,
  appendBaselineEvents,
  type BaselineRow,
  type BaselineKind,
} from '../services/baselinesService';
import { buildBaselineChannelPairs } from '../utils/baselineSavePayload';

export const NEW_BASELINE_OPTION = '__NEW_BASELINE__';

export interface UseBaselineSaveOptions {
  getClassName: Accessor<string>;
  getProjectId: Accessor<number | string>;
  /**
   * The page's `chartGroups()` — the source of the channel/aggregate pairs to fit. Straight-line
   * saves need it; maneuver saves omit it (the server fits every numeric maneuver channel).
   */
  getChartGroups?: Accessor<unknown[]>;
  /** The page-level x-axis, used as the fallback for a series that inherits it. */
  getPageXAxis?: Accessor<string>;
  /**
   * Events currently visible after cut / Uw-Dw / filters, used when a cut is active. Ids or row
   * objects carrying `event_id` — `normalizeIds` accepts either, which is what lets the maneuver
   * pages pass the store's `filtered()` straight through.
   */
  getVisibleEventIds?: Accessor<unknown[]>;
  /** Which flavour of baseline this page saves. Default 'straight_line'. */
  kind?: BaselineKind;
  /** Audit tag stored on baseline_events rows. Default 'lasso'. */
  source?: string;
  /**
   * A reason saving is not possible right now (e.g. grouped mode, where selection keys are not
   * event ids). Non-null shows the reason as a notice instead of opening the modal.
   */
  getSaveBlockReason?: () => string | null;
  /** Called after a successful save so the page can reload and redraw the baseline. */
  onSaved?: (info: { baselineId: number; name: string; created: boolean }) => void | Promise<void>;
}

function normalizeIds(raw: unknown): number[] {
  if (!Array.isArray(raw)) { return []; }
  const out = new Set<number>();
  for (const entry of raw) {
    const id = typeof entry === 'number'
      ? entry
      : Number((entry as { event_id?: unknown })?.event_id ?? entry);
    if (Number.isInteger(id) && id > 0) { out.add(id); }
  }
  return [...out];
}

export function useBaselineSave(options: UseBaselineSaveOptions) {
  const [showSaveModal, setShowSaveModal] = createSignal(false);
  const [baselines, setBaselines] = createSignal<BaselineRow[]>([]);
  const [selectedKey, setSelectedKey] = createSignal('');
  const [isNamingNew, setIsNamingNew] = createSignal(false);
  const [newName, setNewName] = createSignal('');
  const [saving, setSaving] = createSignal(false);
  const [saveError, setSaveError] = createSignal<string | null>(null);
  const [saveMessage, setSaveMessage] = createSignal<string | null>(null);
  const [hotkeyNotice, setHotkeyNotice] = createSignal<string | null>(null);

  let noticeTimer: ReturnType<typeof setTimeout> | null = null;

  const showNotice = (message: string) => {
    setHotkeyNotice(message);
    if (noticeTimer != null) { clearTimeout(noticeTimer); }
    noticeTimer = setTimeout(() => { setHotkeyNotice(null); noticeTimer = null; }, 8000);
  };

  const dismissNotice = () => {
    if (noticeTimer != null) { clearTimeout(noticeTimer); noticeTimer = null; }
    setHotkeyNotice(null);
  };

  const resetModal = () => {
    setIsNamingNew(false);
    setNewName('');
    setSelectedKey('');
    setSaveError(null);
    setSaveMessage(null);
  };

  /**
   * Which events to save: an explicit selection wins; otherwise, if the user has cut down to a
   * subset, everything still visible. No selection at all is not an error, just nothing to save.
   */
  const resolveEventIds = (): number[] => {
    const selected = normalizeIds(selectedEvents());
    if (selected.length > 0) { return selected; }
    if (normalizeIds(cutEvents()).length > 0 && options.getVisibleEventIds) {
      return normalizeIds(options.getVisibleEventIds());
    }
    return [];
  };

  const selectedEventCount = () => resolveEventIds().length;

  const kind: BaselineKind = options.kind || 'straight_line';
  const source = options.source || 'lasso';

  const openSaveModal = async () => {
    const blockReason = options.getSaveBlockReason?.();
    if (blockReason) {
      setShowSaveModal(false);
      resetModal();
      showNotice(blockReason);
      return;
    }

    const eventIds = resolveEventIds();
    if (eventIds.length === 0) {
      setShowSaveModal(false);
      resetModal();
      showNotice('Select events on the scatter charts (lasso or click), or cut a selection, then press + to save a baseline.');
      return;
    }

    dismissNotice();
    resetModal();
    setShowSaveModal(true);

    const className = options.getClassName();
    const projectId = options.getProjectId();
    if (!className || !projectId) {
      setSaveError('Project context is not available.');
      return;
    }

    try {
      // Every baseline in the project. Filtering by kind hid performance baselines on the maneuver
      // pages, so a name the user already had could not be selected and was then rejected as a duplicate.
      const rows = await listBaselines(className, projectId);
      setBaselines(rows);
      if (rows.length > 0) {
        setSelectedKey(String(rows[0].baseline_id));
      } else {
        setSelectedKey(NEW_BASELINE_OPTION);
        setIsNamingNew(true);
      }
    } catch (err: unknown) {
      logError('[useBaselineSave] Failed to load baselines', err);
      setSaveError('Could not load baselines.');
    }
  };

  const closeSaveModal = () => {
    setShowSaveModal(false);
    resetModal();
  };

  /**
   * `+` opens the save modal. Returns true when the key was handled so the caller can stop there.
   *
   * Admin-only, matching the other export-style actions on these pages. The server enforces write
   * permission independently — this only keeps the hotkey out of the way for everyone else.
   */
  const handleSaveHotkey = (event: KeyboardEvent): boolean => {
    if (event.key !== '+' && event.key !== '=' && event.code !== 'NumpadAdd') { return false; }

    // Never steal the key from a text field, or from another modal that is already open.
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || target?.isContentEditable) {
      return false;
    }
    if (typeof document !== 'undefined' && document.querySelector('.pagesettings-modal')) {
      return false;
    }

    event.preventDefault();
    if (!isAdministrator(user())) { return true; }
    void openSaveModal();
    return true;
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaveMessage(null);

    const className = options.getClassName();
    const projectId = options.getProjectId();
    if (!className || !projectId) {
      setSaveError('Project context is not available.');
      return;
    }

    const eventIds = resolveEventIds();
    if (eventIds.length === 0) {
      setSaveError('Select one or more events before saving.');
      return;
    }

    const key = selectedKey();
    const namingNew = isNamingNew() || key === NEW_BASELINE_OPTION;

    // First Save on "New baseline" turns the picker into a name box; the second one commits.
    if (namingNew && !isNamingNew()) {
      setIsNamingNew(true);
      return;
    }

    // Maneuver fits cover every numeric channel server-side, so there are no pairs to send.
    const channels = kind === 'maneuver'
      ? []
      : buildBaselineChannelPairs(options.getChartGroups?.() ?? [], options.getPageXAxis?.());

    const reportSaved = async (
      baselineId: number,
      name: string,
      created: boolean,
      data: { events_added?: number; events_rejected?: number } | undefined
    ) => {
      const rejected = typeof data?.events_rejected === 'number' ? data.events_rejected : 0;
      const added = typeof data?.events_added === 'number' ? data.events_added : eventIds.length;
      if (added === 0 && rejected > 0) {
        setSaveError('None of the selected events could be saved to that baseline.');
        return;
      }
      setSaveMessage(`Saved ${added} event(s) to baseline "${name}".`);
      if (Number.isInteger(baselineId) && baselineId > 0 && options.onSaved) {
        await options.onSaved({ baselineId, name, created });
      }
      closeSaveModal();
    };

    setSaving(true);
    try {
      if (namingNew) {
        const name = newName().trim();
        if (!name) {
          setSaveError('Enter a name for the new baseline.');
          return;
        }
        // The name box is how you reach a baseline that already exists, not only how you mint one.
        const existing = baselines().find((b) => b.name.trim().toLowerCase() === name.toLowerCase());
        if (existing) {
          const result = await appendBaselineEvents({
            className, projectId, baselineId: existing.baseline_id, eventIds, channels, source,
          });
          if (!result.success) {
            setSaveError(result.message || 'Failed to save events to baseline.');
            return;
          }
          logDebug('[useBaselineSave] Appended to existing baseline by name', result.data);
          await reportSaved(existing.baseline_id, existing.name, false, result.data);
          return;
        }
        const result = await createBaseline({
          className, projectId, name, eventIds, channels, kind, source,
        });
        if (!result.success) {
          setSaveError(result.message || 'Failed to create baseline.');
          return;
        }
        logDebug('[useBaselineSave] Created baseline', result.data);
        const createdId = Number(result.data?.baseline_id);
        const reused = Boolean(result.data?.reused);
        await reportSaved(createdId, name, !reused, result.data);
        return;
      }

      const baselineId = Number(key);
      if (!Number.isInteger(baselineId) || baselineId <= 0) {
        setSaveError('Select a baseline to save to.');
        return;
      }
      const name = baselines().find((b) => b.baseline_id === baselineId)?.name || 'baseline';
      const result = await appendBaselineEvents({
        className, projectId, baselineId, eventIds, channels, source,
      });
      if (!result.success) {
        setSaveError(result.message || 'Failed to save events to baseline.');
        return;
      }
      logDebug('[useBaselineSave] Appended baseline events', result.data);
      await reportSaved(baselineId, name, false, result.data);
    } catch (err: unknown) {
      logError('[useBaselineSave] Save failed', err);
      setSaveError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectedKeyChange = (value: string) => {
    setSelectedKey(value);
    setIsNamingNew(false);
    setNewName('');
  };

  return {
    showSaveModal,
    baselines,
    selectedKey,
    setSelectedKey: handleSelectedKeyChange,
    isNamingNew,
    newName,
    setNewName,
    saving,
    saveError,
    saveMessage,
    hotkeyNotice,
    dismissNotice,
    selectedEventCount,
    openSaveModal,
    closeSaveModal,
    handleSaveHotkey,
    handleSave,
  };
}
